import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  IconButton,
  TextField,
  Button,
  Divider,
  Chip,
  CircularProgress,
  Autocomplete,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { Icon } from "../../icons";
import { COLORS, TYPOGRAPHY } from "../../constants/color";
import axios from "axios";
import { useAuth } from "../../context/authContext";
import { url } from "../../constants/url";
const STANDARD_PLANS = [
  { _id: "plan_15", planName: "15 Credits", credits: 15, amount: 24999, days: 30 },
  { _id: "plan_40", planName: "40 Credits", credits: 40, amount: 54999, days: 60 },
  { _id: "plan_75", planName: "75 Credits", credits: 75, amount: 89999, days: 90 },
];

const AddCreditsModal = ({
  open,
  onClose,
  client: initialClient = null,
  onApprove,
  showToast, // Add this prop
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const { token } = useAuth();
  const [selectedClient, setSelectedClient] = useState(initialClient);
  const [selectedCredits, setSelectedCredits] = useState("other");
  const [amount, setAmount] = useState("");
  const [customCredits, setCustomCredits] = useState("");
  const [customAmount, setCustomAmount] = useState("");
  const [customDays, setCustomDays] = useState("");
  const [planType, setPlanType] = useState("NORMAL"); // "NORMAL" or "OUTSTATION"
  const [adminComment, setAdminComment] = useState("");

  const [clientsList, setClientsList] = useState([]);
  const [creditPlans, setCreditPlans] = useState([]);
  const [loadingClients, setLoadingClients] = useState(false);
  const [loadingPlans, setLoadingPlans] = useState(false);
  const [loading, setLoading] = useState(false);

  // REMOVED local toast state

  const inputSx = {
    "& .MuiOutlinedInput-root": {
      height: 50,
      borderRadius: 1,

      "& fieldset": {
        borderColor: COLORS.PRIMARY,
      },

      "&:hover fieldset": {
        borderColor: COLORS.PRIMARY,
      },

      "&.Mui-focused fieldset": {
        borderColor: COLORS.PRIMARY,
      },
    },

    "& input": {
      color: COLORS.PRIMARY,
      fontWeight: 500,
    },

    "& input::placeholder": {
      color: COLORS.LIGHT_SECONDARY,
      opacity: 1,
    },
  };

  // Custom style for disabled inputs when no client selected
  const disabledInputSx = {
    "& .MuiOutlinedInput-root": {
      height: 50,
      borderRadius: 1,
      "& fieldset": {
        borderColor: "#E0E0E0",
      },
      "&:hover fieldset": {
        borderColor: "#E0E0E0",
      },
      "&.Mui-focused fieldset": {
        borderColor: "#E0E0E0 !important",
      },
    },
    "& input": {
      color: "#B0B0B0",
      fontWeight: 500,
    },
    "& input::placeholder": {
      color: "#B0B0B0",
      opacity: 1,
    },
    "& .MuiAutocomplete-input": {
      color: "#B0B0B0 !important",
    },
  };

  // Custom style for prefilled amount field
  const prefilledAmountSx = {
    "& .MuiOutlinedInput-root": {
      height: 50,
      borderRadius: 1,
      "& fieldset": {
        borderColor: COLORS.PRIMARY,
      },
      "&:hover fieldset": {
        borderColor: COLORS.PRIMARY,
      },
      "&.Mui-focused fieldset": {
        borderColor: COLORS.PRIMARY,
      },
      "&.Mui-disabled": {
        "& fieldset": {
          borderColor: COLORS.PRIMARY,
          backgroundColor: "rgba(25, 118, 210, 0.04)",
        },
        "& input": {
          color: COLORS.PRIMARY,
          fontWeight: 500,
          "-webkit-text-fill-color": COLORS.PRIMARY + " !important",
        },
        "&:hover fieldset": {
          borderColor: COLORS.PRIMARY,
        },
      },
    },
    "& input": {
      color: COLORS.PRIMARY,
      fontWeight: 500,
    },
    "& .MuiInputBase-input.Mui-disabled": {
      WebkitTextFillColor: COLORS.PRIMARY + " !important",
      opacity: 1,
    },
  };

  // Style for amount field when no client selected (to remove focus highlight)
  const amountFieldNoClientSx = {
    "& .MuiOutlinedInput-root": {
      height: 50,
      borderRadius: 1,
      "& fieldset": {
        borderColor: "#E0E0E0",
      },
      "&:hover fieldset": {
        borderColor: "#E0E0E0",
      },
      "&.Mui-focused fieldset": {
        borderColor: "#E0E0E0 !important",
        boxShadow: "none !important",
      },
    },
    "& input": {
      color: "#B0B0B0",
      fontWeight: 500,
    },
    "& input::placeholder": {
      color: "#B0B0B0",
      opacity: 1,
    },
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: "#E0E0E0 !important",
    },
  };

  // Fetch approved/active clients from backend
  const fetchApprovedClients = async () => {
    try {
      setLoadingClients(true);
      const response = await axios.get(`${url}/api/users/fetch`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          role: "CLIENT",
          limit: 1000,
        },
      });

      if (response.data?.users) {
        setClientsList(
          response.data.users.filter(
            u =>
              u.clientRole?.roleType !== "Individual" &&
              u.status !== "PENDING" &&
              u.status !== "REJECTED"
          )
        );
      }
    } catch (err) {
      console.error("Error fetching clients:", err);
      // Use showToast prop instead of local showError
      if (showToast) {
        showToast("error", "Error", "Failed to load clients");
      }
      setClientsList([]);
    } finally {
      setLoadingClients(false);
    }
  };

  // Fetch credit plans from backend API only
  const fetchCreditPlans = async () => {
    try {
      setLoadingPlans(true);
      const response = await axios.get(`${url}/api/credits/fetch`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      // Show ONLY STANDARD_PLANS and "Other" as requested by user
      const finalPlans = [
        ...STANDARD_PLANS,
        { _id: "other", planName: "Other", credits: 0, amount: 0, days: 30 }
      ];
      setCreditPlans(finalPlans);
    } catch (err) {
      console.error("Error fetching credit plans:", err);
      if (showToast) {
        showToast("error", "Error", "Failed to load credit plans");
      }
      setCreditPlans([...STANDARD_PLANS, { _id: "other", planName: "Other", credits: 0, amount: 0, days: 30 }]);
    } finally {
      setLoadingPlans(false);
    }
  };

  // REMOVED local showSuccess and showError functions

  // Initialize with initial client if provided
  useEffect(() => {
    if (initialClient) {
      setSelectedClient(initialClient);
    }
  }, [initialClient]);

  // Fetch data when modal opens
  useEffect(() => {
    if (open) {
      fetchApprovedClients();
      fetchCreditPlans();
      // REMOVED local toast clearing
      if (!initialClient) {
        setSelectedClient(null);
      }
      setSelectedCredits("other");
      setAmount("");
      setCustomCredits("");
      setCustomAmount("");
      setCustomDays("");
      setPlanType("NORMAL");
      setAdminComment("");
    }
  }, [open]);

  // Get selected plan object
  const getSelectedPlan = () => {
    if (!selectedCredits) return null;
    if (selectedCredits === "other") {
      return { _id: "other", planName: "Other", credits: 0, amount: 0, days: 30 };
    }
    return creditPlans.find(plan => plan._id === selectedCredits) || null;
  };

  // Handle plan selection
  const handlePlanSelect = (plan) => {
    if (!plan) {
      setSelectedCredits("other");
      setAmount("");
      setCustomCredits("");
      setCustomAmount("");
      setCustomDays("");
      return;
    }

    const planId = plan._id;
    setSelectedCredits(planId);

    if (planId === "other") {
      setAmount("");
      setCustomCredits("");
      setCustomAmount("");
      setCustomDays("");
    } else {
      // For predefined plans, auto-fill the amount, credits and days
      if (planType === "NORMAL") {
        setAmount(plan.amount.toString());
        setCustomCredits(plan.credits.toString());
        setCustomDays(plan.days ? plan.days.toString() : (plan.months ? (plan.months * 30).toString() : ""));
      } else {
        // For outstation, 1 credit = 8000
        const credits = plan.credits || 0;
        setCustomCredits(credits.toString());
        setCustomAmount((credits * 8000).toString());
        setCustomDays("30"); // Default validity for outstation
      }
    }
  };

  // Handle credits change for outstation or "other"
  const handleCreditsChange = (val) => {
    setCustomCredits(val);
    if (planType === "OUTSTATION") {
      const credits = parseInt(val) || 0;
      setCustomAmount((credits * 8000).toString());
    }
  };

  // Calculate remaining credits
  const calculateRemainingCredits = (client) => {
    if (!client) return { normal: "-", outstation: "-" };

    const used = client.credits?.usedCredits || 0;
    const remaining = client.credits?.totalCredits || 0;
    const total = used + remaining;

    const outstationUsed = client.credits?.outstationCredits?.usedCredits || 0;
    const outstationRemaining = client.credits?.outstationCredits?.totalCredits || 0;
    const outstationTotal = outstationUsed + outstationRemaining;

    return {
      normal: total === 0 && used === 0 ? "-" : `${remaining}/${total}`,
      outstation: outstationTotal === 0 && outstationUsed === 0 ? "-" : `${outstationRemaining}/${outstationTotal}`
    };
  };

  const approvedCredits =
    selectedCredits === "other"
      ? customCredits
      : getSelectedPlan()?.credits || "";

  const approvedName = selectedClient?.name || "";

  // Format status for display
  const getStatusText = (status) => {
    switch (status) {
      case 'APPROVED': return 'Active';
      case 'PENDING': return 'Pending';
      case 'REJECTED': return 'Rejected';
      default: return status || 'Pending';
    }
  };

  // Check if approve button should be enabled
  const isApproveEnabled = () => {
    if (selectedCredits === "other" || planType === "OUTSTATION") {
      // For "Other" or Outstation, credits are required
      // For Outstation, validity is not required from user
      const creditsValid = !!customCredits && customCredits !== "0";
      const daysValid = planType === "OUTSTATION" ? true : (!!customDays && customDays !== "0");
      return creditsValid && daysValid;
    } else {
      // For predefined plans, just need a selection
      return true;
    }
  };

  // Handle approve button click
  const handleApprove = async () => {
    try {
      setLoading(true);

      if (!selectedClient) {
        if (showToast) {
          showToast("error", "Error", "Please select a client");
        }
        setLoading(false);
        return;
      }

      // For Outstation, we implicitly use "other" (custom entry)
      const currentSelectedCredits = planType === "OUTSTATION" ? "other" : selectedCredits;

      if (!currentSelectedCredits) {
        if (showToast) {
          showToast("error", "Error", "Please select a credit plan");
        }
        setLoading(false);
        return;
      }

      // Always use currentSelectedCredits from here on
      const finalSelectedCredits = currentSelectedCredits;

      let creditPlanID = null;
      let otherPlan = null;
      let creditsToAdd = 0;

      if (finalSelectedCredits === "other" || finalSelectedCredits.startsWith("plan_")) {
        const selectedPlan = getSelectedPlan();

        // Redefine getSelectedPlan locally if needed or just use logic
        const planObj = finalSelectedCredits === "other"
          ? { _id: "other", planName: "Other", credits: 0, amount: 0, days: 30 }
          : creditPlans.find(p => p._id === finalSelectedCredits);

        // Determine values based on whether it's "other" or a standard plan
        const credits = finalSelectedCredits === "other" ? parseInt(customCredits) : planObj.credits;
        const amt = finalSelectedCredits === "other" ? (customAmount ? parseInt(customAmount) : 0) : planObj.amount;
        const days = finalSelectedCredits === "other" ? (customDays ? parseInt(customDays) : 30) : (planObj.days || 30);
        const name = finalSelectedCredits === "other" ? "Custom Plan" : planObj.planName;

        if (finalSelectedCredits === "other" && (!credits || credits === 0)) {
          if (showToast) {
            showToast("error", "Error", "Please enter number of credits for custom plan");
          }
          setLoading(false);
          return;
        }

        otherPlan = {
          credits: credits,
          amount: amt,
          days: days,
          planName: name,
        };
        creditsToAdd = credits;
        creditPlanID = null;
      } else {
        creditPlanID = finalSelectedCredits;
        const selectedPlan = creditPlans.find(p => p._id === finalSelectedCredits);
        if (!selectedPlan) {
          if (showToast) {
            showToast("error", "Error", "Selected plan not found");
          }
          setLoading(false);
          return;
        }
      }

      const response = await axios.post(
        `${url}/api/admin/add-credits`,
        {
          clientID: selectedClient._id,
          creditPlanID,
          otherPlan,
          planType,
          adminComment,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.status === 200) {
        // Use showToast prop instead of local showSuccess
        if (showToast) {
          const displayCredits = finalSelectedCredits === "other" ? `${creditsToAdd} Credits` : (getSelectedPlan()?.planName || `${creditsToAdd} Credits`);
          const displayAmount = finalSelectedCredits === "other" ? (customAmount || "0") : (getSelectedPlan()?.amount?.toLocaleString() || "0");
          const isQueuedStatus = response.data.isQueued;

          let toastTitle = "Credits Added";
          let toastMessage = `${selectedClient.name} - Client has been added ${displayCredits} (₹${displayAmount})`;

          if (isQueuedStatus) {
            toastTitle = "Plan Queued";
            toastMessage = `${selectedClient.name} - Plan added to queue. This credits will be active once the first plan/credits expire.`;
          }

          showToast("success", toastTitle, toastMessage);
        }

        if (onApprove) {
          onApprove(selectedClient, selectedCredits, amount);
        }

        setTimeout(() => {
          onClose();
        }, 1500);
      }
    } catch (err) {
      console.error("Error adding credits:", err);
      // Use showToast prop instead of local showError
      if (showToast) {
        showToast("error", "Error", err.response?.data?.message || "Failed to add credits");
      }
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  return (
    <>
      {/* Overlay */}
      <Box
        onClick={onClose}
        sx={{
          position: "fixed",
          inset: 0,
          bgcolor: "rgba(0,0,0,0.5)",
          zIndex: 1400,
        }}
      />

      {/* Modal */}
      <Box
        sx={{
          position: "fixed",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: isMobile ? '90vw' : isTablet ? '85vw' : 730,
          maxWidth: '95vw',
          maxHeight: '90vh',
          overflowY: 'auto',
          bgcolor: "#fff",
          borderRadius: 2,
          p: isMobile ? 2 : 3,
          zIndex: 1401,
          boxShadow: "0px 12px 30px rgba(0,0,0,0.2)",
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: isMobile ? 2 : 3,
            flexWrap: 'wrap',
            gap: 1,
          }}
        >
          <Typography sx={{
            ...TYPOGRAPHY.H3,
            color: COLORS.PRIMARY,
            fontSize: isMobile ? '1.25rem' : TYPOGRAPHY.H3.fontSize
          }}>
            Add Credits to Client:
          </Typography>
          <IconButton onClick={onClose} sx={{ p: 1 }}>
            <Icon name="close" color={COLORS.ERROR} />
          </IconButton>
        </Box>

        {/* Credit Type Selection */}
        <Box sx={{ mb: isMobile ? 2 : 3 }}>
          <Typography sx={{
            ...TYPOGRAPHY.H4,
            color: COLORS.PRIMARY,
            fontSize: isMobile ? '0.875rem' : TYPOGRAPHY.H4.fontSize,
            mb: 1
          }}>
            Select Credit Type
          </Typography>
          <RadioGroup
            row
            value={planType}
            onChange={(e) => {
              setPlanType(e.target.value);
              // Reset selection when type changes
              setSelectedCredits("other");
              setAmount("");
              setCustomCredits("");
              setCustomAmount("");
              setCustomDays("");

              if (e.target.value === "OUTSTATION") {
                setSelectedCredits("other");
                setCustomDays("30");
              }
            }}
          >
            <FormControlLabel
              value="NORMAL"
              control={<Radio sx={{ color: COLORS.PRIMARY, '&.Mui-focused': { color: COLORS.PRIMARY } }} />}
              label={<Typography sx={{ fontSize: isMobile ? '13px' : '14px', color: COLORS.TEXT }}>Normal Credits</Typography>}
            />
            <FormControlLabel
              value="OUTSTATION"
              control={<Radio sx={{ color: COLORS.PRIMARY, '&.Mui-focused': { color: COLORS.PRIMARY } }} />}
              label={<Typography sx={{ fontSize: isMobile ? '13px' : '14px', color: COLORS.TEXT }}>Outstation Credits</Typography>}
            />
          </RadioGroup>
          {planType === "OUTSTATION" && (
            <Typography sx={{ fontSize: isMobile ? '11px' : '13px', color: COLORS.SECONDARY, mt: 0.5, fontStyle: 'italic', fontWeight: 500 }}>
              Pricing: ₹8,000 per day + transportation + food per credit
            </Typography>
          )}
        </Box>

        {/* Inputs Row */}
        <Box sx={{
          display: "flex",
          gap: isMobile ? 1 : 2,
          mb: isMobile ? 2 : 3,
          flexDirection: isMobile ? 'column' : 'row'
        }}>
          {/* Search Client */}
          <Box sx={{
            display: "flex",
            flexDirection: "column",
            gap: 0.5,
            flex: 1,
            minWidth: isMobile ? '100%' : 'auto'
          }}>
            <Typography sx={{
              ...TYPOGRAPHY.H4,
              color: COLORS.PRIMARY,
              fontSize: isMobile ? '0.875rem' : TYPOGRAPHY.H4.fontSize
            }}>
              Search Client by Name
            </Typography>
            <Autocomplete
              value={selectedClient}
              onChange={(event, newValue) => {
                setSelectedClient(newValue);
                // Only reset if not in Outstation mode
                if (planType !== "OUTSTATION") {
                  setSelectedCredits("other");
                  setAmount("");
                  setCustomCredits("");
                  setCustomAmount("");
                }
              }}
              options={clientsList}
              getOptionLabel={(option) => option?.name || ""}
              loading={loadingClients}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Search client by name"
                  variant="outlined"
                  size="small"
                  sx={inputSx}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingClients ? <CircularProgress size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              renderOption={(props, option) => (
                <Box component="li" {...props} key={option._id}>
                  <Box sx={{ width: "100%", py: 0.5 }}>
                    <Typography sx={{ fontWeight: 500, fontSize: '14px' }}>
                      {option.name}
                    </Typography>
                    <Typography sx={{ fontSize: '12px', color: COLORS.LIGHT_SECONDARY }}>
                      {option.email}
                    </Typography>
                  </Box>
                </Box>
              )}
              noOptionsText="No clients found"
              isOptionEqualToValue={(option, value) => option._id === value?._id}
              componentsProps={{
                popper: {
                  sx: {
                    zIndex: 9999,
                  },
                },
              }}
            />
          </Box>

          {/* Add Credits / Outstation Inputs */}
          <Box sx={{
            display: "flex",
            flexDirection: "column",
            gap: 0.5,
            flex: 1,
            minWidth: isMobile ? '100%' : 'auto'
          }}>
            <Typography sx={{
              ...TYPOGRAPHY.H4,
              color: COLORS.PRIMARY,
              fontSize: isMobile ? '0.875rem' : TYPOGRAPHY.H4.fontSize
            }}>
              {planType === "NORMAL" ? "Add Credits*" : "No. of Outstation Credits*"}
            </Typography>
            {planType === "NORMAL" ? (
              <Autocomplete
                value={getSelectedPlan()}
                onChange={(event, newValue) => handlePlanSelect(newValue)}
                options={creditPlans}
                getOptionLabel={(option) => {
                  if (!option) return "";
                  if (option._id === "other") return "Other";
                  return `${option.credits} Credits `;
                }}
                loading={loadingPlans}
                disabled={!selectedClient || loadingPlans}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="No. of credits"
                    variant="outlined"
                    size="small"
                    sx={!selectedClient ? disabledInputSx : inputSx}
                    InputProps={{
                      ...params.InputProps,
                      readOnly: !selectedClient,
                      endAdornment: (
                        <>
                          {loadingPlans ? <CircularProgress size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                renderOption={(props, option) => (
                  <Box component="li" {...props} key={option._id}>
                    <Box sx={{ width: "100%", py: 0.5, display: "flex", justifyContent: "space-between" }}>
                      <Typography sx={{ fontWeight: 500, fontSize: "14px" }}>
                        {option._id === "other" ? "Other" : `${option.credits} credits`}
                      </Typography>
                      {option._id !== "other" && (
                        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end" }}>
                          <Typography sx={{ fontSize: "12px", color: COLORS.SECONDARY }}>
                            ₹{option.amount.toLocaleString()}
                          </Typography>
                          <Typography sx={{ fontSize: "10px", color: COLORS.LIGHT_SECONDARY }}>
                            {option.days || (option.months * 30)} days
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Box>
                )}
                noOptionsText="No credit plans found"
                isOptionEqualToValue={(option, value) => option._id === value?._id}
                componentsProps={{
                  popper: {
                    sx: {
                      zIndex: 9999,
                    },
                  },
                }}
              />
            ) : (
              <TextField
                value={customCredits}
                onChange={(e) => handleCreditsChange(e.target.value)}
                disabled={!selectedClient}
                placeholder="Enter credits"
                size="small"
                sx={!selectedClient ? disabledInputSx : inputSx}
              />
            )}
          </Box>

          {/* Amount Received - Now Optional */}
          <Box sx={{
            display: "flex",
            flexDirection: "column",
            gap: 0.5,
            minWidth: isMobile ? '100%' : 'auto'
          }}>
            <Typography sx={{
              ...TYPOGRAPHY.H4,
              color: COLORS.PRIMARY,
              fontSize: isMobile ? '0.875rem' : TYPOGRAPHY.H4.fontSize
            }}>
              Amount Received
            </Typography>
            <TextField
              value={selectedCredits === "other" ? customAmount : amount}
              onChange={(e) => {
                if (!selectedClient) return; // Prevent changes when no client selected
                const value = e.target.value;
                if (value === '' || /^\d+$/.test(value)) {
                  if (selectedCredits === "other") {
                    setCustomAmount(value);
                  } else {
                    setAmount(value);
                  }
                }
              }}
              disabled={!selectedClient || (selectedCredits && selectedCredits !== "other")}
              placeholder={
                selectedCredits === "other"
                  ? "Enter amount"
                  : selectedCredits
                    ? "Auto-filled"
                    : "Amount in Rs."
              }
              size="small"
              sx={
                !selectedClient
                  ? amountFieldNoClientSx
                  : (selectedCredits && selectedCredits !== "other")
                    ? prefilledAmountSx
                    : inputSx
              }
              InputProps={{
                readOnly: !selectedClient,
                endAdornment: selectedCredits && (selectedCredits === "other" ? customAmount : amount) ? (
                  <Typography sx={{ fontSize: '12px', color: COLORS.LIGHT_SECONDARY, mr: 1 }}>
                    ₹
                  </Typography>
                ) : null,
              }}
            />
          </Box>
        </Box>

        {/* Validity row - Show for normal "Other" only */}
        {planType === "NORMAL" && selectedCredits === "other" && selectedClient && (
          <Box sx={{
            display: "flex",
            gap: isMobile ? 1 : 2,
            mb: isMobile ? 2 : 3,
            flexDirection: isMobile ? 'column' : 'row'
          }}>
            <Box sx={{
              display: "flex",
              flexDirection: "column",
              gap: 0.5,
              flex: 1,
              minWidth: isMobile ? '100%' : 'auto'
            }}>
              <Typography sx={{
                ...TYPOGRAPHY.H4,
                color: COLORS.PRIMARY,
                fontSize: isMobile ? '0.875rem' : TYPOGRAPHY.H4.fontSize
              }}>
                Number of Credits*
              </Typography>
              <TextField
                value={customCredits}
                onChange={(e) => handleCreditsChange(e.target.value)}
                disabled={!selectedClient}
                placeholder="Enter number of credits"
                size="small"
                sx={inputSx}
              />
            </Box>

            <Box sx={{
              display: "flex",
              flexDirection: "column",
              gap: 0.5,
              flex: 1,
              minWidth: isMobile ? '100%' : 'auto'
            }}>
              <Typography sx={{
                ...TYPOGRAPHY.H4,
                color: COLORS.PRIMARY,
                fontSize: isMobile ? '0.875rem' : TYPOGRAPHY.H4.fontSize
              }}>
                Validity (Days)*
              </Typography>
              <TextField
                value={customDays}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === '' || /^\d+$/.test(value)) {
                    setCustomDays(value);
                  }
                }}
                disabled={!selectedClient}
                placeholder="Enter validity in days"
                size="small"
                sx={inputSx}
              />
            </Box>
          </Box>
        )}

        {/* Admin Comment field */}
        {selectedClient && (
          <Box sx={{ mb: isMobile ? 2 : 3 }}>
            <Typography sx={{
              ...TYPOGRAPHY.H4,
              color: COLORS.PRIMARY,
              fontSize: isMobile ? '0.875rem' : TYPOGRAPHY.H4.fontSize,
              mb: 0.5
            }}>
              Admin Comment (Optional)
            </Typography>
            <TextField
              value={adminComment}
              onChange={(e) => setAdminComment(e.target.value)}
              placeholder="Add a note for this adjustment"
              variant="outlined"
              size="small"
              multiline
              minRows={2}
              fullWidth
              sx={{
                ...inputSx,
                "& .MuiOutlinedInput-root": {
                  ...inputSx["& .MuiOutlinedInput-root"],
                  height: 'auto',
                  minHeight: 50,
                  py: 1.5
                }
              }}
            />
          </Box>
        )}

        {/* First Divider - Only show when client is selected */}
        {selectedClient && (
          <>
            <Divider sx={{ mb: isMobile ? 2 : 3, borderColor: COLORS.LIGHT_SECONDARY }} />

            {/* Client Info Preview */}
            <Box sx={{ mb: isMobile ? 2 : 3 }}>
              <Box>
                {/* TOP ROW: Name + Joined Date */}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 1,
                    flexWrap: 'wrap',
                    gap: 1
                  }}
                >
                  {/* Name + Status */}
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <Icon name="profile" color={COLORS.PRIMARY} />
                    <Typography
                      component="div"
                      sx={{
                        ...TYPOGRAPHY.H3,
                        color: COLORS.TEXT,
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 1
                      }}
                    >
                      {selectedClient.name}
                      <Chip
                        label={getStatusText(selectedClient.status)}
                        size="small"
                        sx={{
                          bgcolor: selectedClient.status === "APPROVED" ? "#8CD47E" : "#F8D66D",
                          color: "#fff",
                          height: 18,
                          fontSize: '10px',
                          ...TYPOGRAPHY.BODY_SMALL
                        }}
                      />
                    </Typography>
                  </Box>

                  {/* Joined Date */}
                  <Typography sx={{ fontSize: isMobile ? '12px' : '14px', color: COLORS.TEXT }}>
                    Joined on:{" "}
                    {new Date(selectedClient.joinedAt || selectedClient.createdAt).toLocaleDateString("en-GB", {
                      day: "2-digit",
                      month: "2-digit",
                      year: "2-digit",
                    }).replace(/\//g, '/')}
                  </Typography>
                </Box>

                {/* Email */}
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5, pl: isMobile ? "0px" : "24px" }}>
                  <Icon name="at" size={isMobile ? 14 : 16} color={COLORS.SECONDARY} />
                  <Typography sx={{ fontSize: isMobile ? '12px' : '14px', color: COLORS.SECONDARY }}>
                    {selectedClient.email}
                  </Typography>
                </Box>

                {/* Phone + Credits (Same Row) */}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: isMobile ? "flex-start" : "center",
                    pl: isMobile ? "0px" : "24px",
                    flexDirection: isMobile ? "column" : "row",
                    gap: isMobile ? 1 : 0
                  }}
                >
                  {/* Phone */}
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Icon name="phoneDone" size={isMobile ? 14 : 16} color={COLORS.SECONDARY} />
                    <Typography sx={{ fontSize: isMobile ? '12px' : '14px', color: COLORS.SECONDARY }}>
                      {selectedClient.phone}
                    </Typography>
                  </Box>

                  {/* Credits (Right Side) - Updated format to show both */}
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: isMobile ? 'flex-start' : 'flex-end' }}>
                    <Typography sx={{ fontSize: isMobile ? '12px' : '14px', color: COLORS.SECONDARY }}>
                      Normal Credits: <strong>{calculateRemainingCredits(selectedClient).normal}</strong>
                    </Typography>
                    <Typography sx={{ fontSize: isMobile ? '12px' : '14px', color: COLORS.SECONDARY }}>
                      Outstation Credits: <strong>{calculateRemainingCredits(selectedClient).outstation}</strong>
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Box>

            {/* Second Divider - Only show when client is selected */}
            <Divider sx={{ mb: isMobile ? 2 : 3, borderColor: COLORS.LIGHT_SECONDARY }} />
          </>
        )}

        {/* Approve Button */}
        <Box sx={{
          display: "flex",
          justifyContent: selectedClient && selectedCredits ? "space-between" : "flex-end",
          alignItems: "center",
          gap: 2,
          flexDirection: isMobile ? "column" : "row"
        }}>
          {/* Left info text - Only show when client and credits are selected */}
          {selectedClient && selectedCredits && (
            <Typography sx={{
              ...TYPOGRAPHY.BODY_LARGE,
              color: COLORS.TEXT,
              flex: 1,
              fontSize: isMobile ? '12px' : 'inherit',
              textAlign: isMobile ? 'center' : 'left'
            }}>
              On Approve:{" "}
              <strong style={{ color: COLORS.PRIMARY }}>
                {approvedCredits}
              </strong>{" "}
              Credits will be added in{" "}
              <strong style={{ color: COLORS.PRIMARY }}>
                {approvedName}
              </strong>
              ’s Account
            </Typography>
          )}

          {/* Right button */}
          <Button
            variant="contained"
            startIcon={<Icon name="check" />}
            fullWidth={isMobile}
            sx={{
              bgcolor: COLORS.PRIMARY,
              "&:hover": { bgcolor: COLORS.PRIMARY },
              minWidth: 120,
              height: isMobile ? 40 : 'auto'
            }}
            disabled={
              !isApproveEnabled() || loading
            }
            onClick={handleApprove}
          >
            {loading ? "Adding..." : "Approve"}
          </Button>
        </Box>
      </Box>

      {/* REMOVED AppToast component from here */}

      {/* Additional CSS fix for dropdown positioning */}
      <style>
        {`
          .MuiAutocomplete-popper,
          .MuiMenu-paper,
          .MuiPopover-root .MuiPaper-root {
            z-index: 9999 !important;
          }
          .MuiModal-root .MuiAutocomplete-popper {
            position: fixed !important;
          }
          .MuiAutocomplete-inputRoot input {
            color: ${COLORS.PRIMARY} !important;
            font-weight: 500;
          }
          /* Hide scrollbar for autocomplete dropdown */
          .MuiAutocomplete-listbox {
            scrollbar-width: none;        /* Firefox */
          }

          .MuiAutocomplete-listbox::-webkit-scrollbar {
            width: 0px;                  /* Chrome, Edge, Safari */
            height: 0px;
          }

          .MuiAutocomplete-listbox::-webkit-scrollbar-thumb {
            background: transparent;
          }
          
          /* Remove focus outline for disabled fields */
          .Mui-disabled .MuiOutlinedInput-notchedOutline {
            border-color: #E0E0E0 !important;
          }
          
          /* Remove blue focus ring */
          .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline {
            border-color: ${COLORS.PRIMARY} !important;
          }
          
          /* Specifically target the amount field when no client is selected */
          .MuiOutlinedInput-root:has(input[readonly]) .MuiOutlinedInput-notchedOutline {
            border-color: #E0E0E0 !important;
          }
          
          .MuiOutlinedInput-root:has(input[readonly]):hover .MuiOutlinedInput-notchedOutline {
            border-color: #E0E0E0 !important;
          }
          
          .MuiOutlinedInput-root:has(input[readonly]).Mui-focused .MuiOutlinedInput-notchedOutline {
            border-color: #E0E0E0 !important;
            box-shadow: none !important;
          }
        `}
      </style>
    </>
  );
};

export default AddCreditsModal;